import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarClock, Plus } from 'lucide-react';
import { IonSegment, IonSegmentButton, IonLabel } from '@ionic/react';
import { Button, EmptyState, ErrorState, Skeleton } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { RequestCard, RideCard } from '../../components/TripCards';
import { api, errorMessage } from '../../lib/api';
import type { RideDto, RideRequestDto } from '../../lib/types';

type Tab = 'requests' | 'upcoming' | 'past';

export function TripsPage() {
  const [tab, setTab] = useState<Tab>('upcoming');
  const requests = useQuery({
    queryKey: ['ride-requests', 'open'],
    queryFn: () => api<RideRequestDto[]>('/ride-requests', { query: { status: 'REQUESTED,INFO_REQUESTED,QUOTED' } }),
  });
  const rides = useQuery({ queryKey: ['rides', tab], queryFn: () => api<RideDto[]>('/rides', { query: { scope: tab === 'past' ? 'past' : 'upcoming' } }), enabled: tab !== 'requests' });
  const q = tab === 'requests' ? requests : rides;

  return (
    <Page
      title="Mis viajes"
      onRefresh={() => Promise.all([requests.refetch(), rides.refetch()])}
      actions={<Link to="/app/request" aria-label="Solicitar viaje" className="tap flex items-center justify-center px-2"><Plus className="size-6" /></Link>}
    >
      <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as Tab)} className="mb-4">
        <IonSegmentButton value="upcoming"><IonLabel>Próximos</IonLabel></IonSegmentButton>
        <IonSegmentButton value="requests">
          <IonLabel>Solicitudes{requests.data?.length ? ` (${requests.data.length})` : ''}</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton value="past"><IonLabel>Historial</IonLabel></IonSegmentButton>
      </IonSegment>

      {q.isLoading && <Skeleton className="h-32" />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      <div className="flex flex-col gap-3">
        {tab === 'requests' && requests.data?.map((r) => <RequestCard key={r.id} r={r} href={`/app/requests/${r.id}`} />)}
        {tab !== 'requests' && rides.data?.map((r) => <RideCard key={r.id} r={r} href={`/app/rides/${r.id}`} />)}
      </div>
      {((tab === 'requests' && requests.data?.length === 0) || (tab !== 'requests' && rides.data?.length === 0)) && (
        <EmptyState
          icon={<CalendarClock className="size-6" />}
          title={tab === 'past' ? 'Aún no tienes viajes realizados' : tab === 'requests' ? 'Sin solicitudes pendientes' : 'No tienes viajes próximos'}
          action={tab !== 'past' && <Link to="/app/request"><Button>Solicitar viaje</Button></Link>}
        />
      )}
    </Page>
  );
}
